import { useContext, useRef, useState } from 'react'
import { Badge, Box, Button, Checkbox, Divider, IconButton, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { SPACING } from '@common/theme/spacing'
import { ThemeContext } from '@common/theme'

interface ModalityViewerSelectorProps {
  modalities: string[]
  modalitiesWithData: string[]
  onOpenViewer: (selectedModalities: string[]) => void
  disabled?: boolean
}

export default function ModalityViewerSelector({
  modalities,
  modalitiesWithData,
  onOpenViewer,
  disabled,
}: ModalityViewerSelectorProps) {
  const [selectedModality, setSelectedModality] = useState<string[] | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const open = Boolean(anchorEl)
  // theme.palette.mode is not set by the custom palette, so read the mode from the app's ThemeContext
  const isDarkMode = useContext(ThemeContext)?.themeMode === 'dark'

  const checkedModalities = selectedModality ?? []
  const hasSelection = checkedModalities.length > 0
  const allSelected = modalitiesWithData.length > 0 && modalitiesWithData.every((m) => checkedModalities.includes(m))
  // no explicit selection means no filter, so the viewer opens everything available
  const effectiveSelection = hasSelection ? checkedModalities : modalitiesWithData

  const handleToggle = () => {
    setAnchorEl(open ? null : containerRef.current)
  }

  const handleModalityToggle = (mod: string) => {
    setSelectedModality(
      checkedModalities.includes(mod) ? checkedModalities.filter((m) => m !== mod) : [...checkedModalities, mod],
    )
  }

  return (
    <Badge
      badgeContent={allSelected ? 'All' : checkedModalities.length}
      color="primary"
      invisible={disabled || !hasSelection}
      sx={{ flexShrink: 0 }}
    >
      <Box
        ref={containerRef}
        display="flex"
        alignItems="stretch"
        flexShrink={0}
        sx={{
          border: '1px solid',
          borderColor: 'secondary.dark',
          borderRadius: (theme) => theme.spacing(SPACING.borderRadiusSm),
          overflow: 'hidden',
        }}
      >
        <Button
          disabled={disabled || modalitiesWithData.length === 0}
          onClick={() => onOpenViewer(effectiveSelection)}
          sx={{
            color: 'text.primary',
            border: 'none',
            py: SPACING.spacingMd,
            px: SPACING.spacingLg,
            borderRadius: 0,
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          Open in viewer
        </Button>
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'secondary.dark', my: 1 }} />
        <Tooltip title="Filter modalities — all open by default">
          <IconButton
            size="small"
            disabled={disabled}
            onClick={handleToggle}
            sx={{
              borderRadius: 0,
              px: SPACING.spacingSm,
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 180,
                mt: 0.5,
                backgroundColor: (theme) =>
                  isDarkMode ? theme.palette.secondary.main : theme.palette.background.default,
                border: (theme) => (isDarkMode ? `1px solid ${alpha(theme.palette.secondary.dark, 0.5)}` : 'none'),
                boxShadow: (theme) => (isDarkMode ? '0px 4px 12px 0px rgba(0, 0, 0, 0.3)' : theme.shadows[4]),
              },
            },
          }}
        >
          <MenuItem onClick={() => setSelectedModality(allSelected ? [] : [...modalitiesWithData])} sx={{ py: 1 }}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={hasSelection && !allSelected}
              disableRipple
              sx={{ p: 0, mr: 1 }}
            />
            <ListItemText>Select all</ListItemText>
          </MenuItem>
          <Divider />
          {modalities.map((mod) => {
            const hasData = modalitiesWithData.includes(mod)
            return (
              <MenuItem key={mod} disabled={!hasData} onClick={() => handleModalityToggle(mod)} sx={{ py: 1 }}>
                <Checkbox size="small" checked={checkedModalities.includes(mod)} disableRipple sx={{ p: 0, mr: 1 }} />
                <ListItemText>{mod}</ListItemText>
              </MenuItem>
            )
          })}
        </Menu>
      </Box>
    </Badge>
  )
}
